// src/config/firebase.ts
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging';

// Configuration pour l'env de développement
// Les valeurs sont chargées depuis les variables d'environnement (.env.development)
const devConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
};

// En développement, alerter si la config Firebase n'est pas chargée (fichier .env.development manquant ou invalide)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development" && !devConfig.apiKey) {
  console.error(
    "⚠️ Configuration Firebase manquante. Créez un fichier .env.development à la racine du projet (copiez .env.example) et remplissez les variables REACT_APP_FIREBASE_*. Redémarrez ensuite le serveur (npm start)."
  );
}


// Configuration pour la production (institut et nettoyage)
const prodConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!
};

// Sélectionner la configuration selon l'environnement
// Pour l'environnement dev (beauty-dev-c5f36.web.app), utiliser la config dev
const isDevEnvironment = typeof window !== 'undefined' && window.location.hostname.includes('beauty-dev-c5f36');
const useDevConfig = process.env.NODE_ENV !== 'production' || isDevEnvironment;
const firebaseConfig = useDevConfig ? devConfig : prodConfig;

// En développement local : refuser de lancer sans clé API pour éviter l'erreur "api-key-not-valid" peu claire
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && useDevConfig && !firebaseConfig.apiKey?.trim()) {
  const msg =
    "Configuration Firebase manquante. Créez .env.development à la racine (copiez .env.example), remplissez REACT_APP_FIREBASE_API_KEY avec la clé de la console Firebase, puis redémarrez le serveur (npm start).";
  console.error("⚠️", msg);
  throw new Error(msg);
}

// Debug: Afficher la configuration utilisée (+ diagnostic env en dev)
if (typeof window !== 'undefined') {
  const apiKeyLen = firebaseConfig.apiKey?.length ?? 0;
  const apiKeyFromEnv = typeof process.env.REACT_APP_FIREBASE_API_KEY === 'string';
  console.log('🔧 Configuration Firebase:', {
    hostname: window.location.hostname,
    nodeEnv: process.env.NODE_ENV,
    isDevEnvironment,
    configUsed: useDevConfig ? 'DEV' : 'PROD',
    projectId: firebaseConfig.projectId,
    ...(process.env.NODE_ENV === 'development' && useDevConfig
      ? {
          envCheck: apiKeyFromEnv ? `REACT_APP_* chargées (apiKey length: ${apiKeyLen})` : 'REACT_APP_FIREBASE_API_KEY absente',
          rappel: '.env.local et .env.development.local écrasent .env.development. Après toute modif .env, redémarrer: npm start',
        }
      : {}),
  });
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
/** Région unique NG (ADR Afrique) — doit matcher `functions/src/config/region.ts`. */
export const functions = getFunctions(app, 'europe-west1');

// ✅ DÉSACTIVÉ : L'emulator n'est pas lancé par défaut
// Pour utiliser l'emulator local, décommenter et lancer : firebase emulators:start
// if (process.env.NODE_ENV === 'development') {
//   connectFunctionsEmulator(functions, 'localhost', 5001);
//   console.log('✅ Connexion à l\'emulator Functions local sur localhost:5001');
// }

// ✅ PHASE 1.1 : Activer Firestore Offline Persistence
// Permet le fonctionnement partiel hors ligne et la synchronisation automatique
if (typeof window !== 'undefined') {
  enableIndexedDbPersistence(db)
    .then(() => {
      console.log('✅ Firestore Offline Persistence activée');
    })
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        // Plusieurs onglets ouverts, persistence déjà activée
        console.warn('⚠️ Firestore Persistence : Plusieurs onglets ouverts ou déjà activée');
      } else if (err.code === 'unimplemented') {
        // Navigateur non supporté (ex: Safari iOS < 11.3)
        console.warn('⚠️ Firestore Persistence non supportée par ce navigateur');
      } else {
        console.error('❌ Erreur lors de l\'activation de Firestore Persistence:', err);
      }
    });
}

/**
 * Obtenir l'URL de la Cloud Function selon l'environnement
 */
export const getCloudFunctionUrl = (functionName: string, region: string = 'us-central1'): string => {
  // En développement local, utiliser l'emulator
  if (process.env.NODE_ENV === 'development') {
    return `http://localhost:5001/${firebaseConfig.projectId}/${region}/${functionName}`;
  }
  
  // En production, utiliser l'URL réelle
  return `https://${region}-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;
};

/** Config web publique (utilisée aussi par le service worker FCM). */
export const getFirebaseWebConfig = () => ({ ...firebaseConfig });

export const getFirebaseVapidKey = (): string =>
  (process.env.REACT_APP_FIREBASE_VAPID_KEY || '').trim();

let messagingPromise: Promise<Messaging | null> | null = null;

/** Messaging FCM web — null si non supporté (Safari ancien, SSR, etc.). */
export const getFirebaseMessaging = (): Promise<Messaging | null> => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!messagingPromise) {
    messagingPromise = isSupported()
      .then((ok) => (ok ? getMessaging(app) : null))
      .catch(() => null);
  }
  return messagingPromise;
};

export default app;